import { ApprovalState, ApprovalType } from '@mg/shared';
import type { PostDisplayState } from '../../lib/display';

/**
 * Pipeline maturity, least → most. Mirrors the API's `aggregateCampaignState` ordering (note
 * REVIEWED sits BELOW COMPLIANCE_CHECK — that's the real two-stage flow order).
 */
const MATURITY_RANK: Record<ApprovalState, number> = {
  [ApprovalState.REJECTED]: 0,
  [ApprovalState.DRAFT]: 1,
  [ApprovalState.REVISE]: 2,
  [ApprovalState.IN_REVIEW]: 3,
  [ApprovalState.REVIEWED]: 4,
  [ApprovalState.COMPLIANCE_CHECK]: 5,
  [ApprovalState.APPROVED]: 6,
  [ApprovalState.SCHEDULED]: 7,
  [ApprovalState.PUBLISHED]: 8,
};

/**
 * The state a CAMPAIGN should display, given its own stored state and the badge states of its
 * posts (compute those with `postBadgeState`).
 *
 * Only NONE mode needs deriving. With approvals off there is no review chain to move a campaign
 * off DRAFT, so it sat at "Draft" forever even once every post was scheduled or published — while
 * the posts themselves correctly showed Scheduled, because `postBadgeState` already derives from
 * the release timestamps in NONE mode. CAMPAIGN mode owns its state directly and POST mode has the
 * server recompute the aggregate, so both are returned untouched.
 *
 * The derived value is the LEAST-mature post state, matching the server's POST-mode aggregate: one
 * unscheduled post holds the campaign at Draft, and it only reads Scheduled once they all are.
 */
export function campaignBadgeState(
  ownState: ApprovalState,
  postStates: ApprovalState[],
  approvalType: ApprovalType,
): ApprovalState {
  if (approvalType !== ApprovalType.NONE || postStates.length === 0) return ownState;
  let least = postStates[0];
  for (const state of postStates) {
    if (MATURITY_RANK[state] < MATURITY_RANK[least]) least = state;
  }
  return least;
}

/**
 * The 7 status tabs shared by the Campaigns hub, the (flat) Posts hub, and the
 * Dashboard pipeline. Approval now lives on the CAMPAIGN, so a Post is filtered
 * by its `campaignApprovalState`. Tab ids are STABLE — the Dashboard pipeline
 * tiles deep-link `drafts` / `approvals` / `approved` / `scheduled` / `published`,
 * so don't rename them.
 */
export type StatusTabId =
  | 'drafts'
  | 'approvals'
  | 'revise'
  | 'approved'
  | 'scheduled'
  | 'published'
  | 'rejected'
  | 'failed';

/** Approval states each tab is responsible for. */
export const TAB_STATES: Record<StatusTabId, ApprovalState[]> = {
  drafts: [ApprovalState.DRAFT],
  // REVIEWED = a Reviewer signed off, awaiting the final Approver (two-stage mode) — still
  // "in review" for hub/tab purposes so the Approver can find it.
  approvals: [ApprovalState.IN_REVIEW, ApprovalState.REVIEWED, ApprovalState.COMPLIANCE_CHECK],
  revise: [ApprovalState.REVISE],
  // Approved but not yet given a schedule date — the "ready to go, not scheduled" bucket.
  approved: [ApprovalState.APPROVED],
  scheduled: [ApprovalState.SCHEDULED],
  published: [ApprovalState.PUBLISHED],
  rejected: [ApprovalState.REJECTED],
  // Derived, not an ApprovalState — callers test isPostFailed() instead of a state list. Kept in
  // the map (empty) so every tab id still has an entry and Record exhaustiveness holds.
  failed: [],
};

/**
 * The status tabs, left to right: work still to do first, finished states last.
 *
 * Failed sits with the unfinished work rather than at the end, because it is the one status that
 * needs a person — parked after Published and Rejected it was the easiest thing on the page to
 * miss. Same order as {@link POST_STATUS_SORT}, so the tab row and the order of posts inside a
 * campaign lane read the same way.
 */
export const TAB_ORDER: { id: StatusTabId; label: string }[] = [
  { id: 'drafts', label: 'Draft' },
  { id: 'approvals', label: 'In Review' },
  { id: 'revise', label: 'Revise' },
  { id: 'approved', label: 'Ready to Schedule' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'failed', label: 'Failed' },
  { id: 'published', label: 'Published' },
  { id: 'rejected', label: 'Rejected' },
];

/** Which tab a single post's chip belongs to, or null if nothing claims it. */
export function tabForState(state: PostDisplayState): StatusTabId | null {
  // Both attention states file under Failed. They read differently on the chip — a delivery failure
  // versus a slot that went by — but they want the same thing from the person, and giving the
  // second one its own tab would hide it from anyone who knows to look in Failed.
  if (state === 'FAILED' || state === 'SCHEDULING_ERROR') return 'failed';
  return (
    TAB_ORDER.find((t) => t.id !== 'failed' && TAB_STATES[t.id].includes(state))?.id ?? null
  );
}

const TAB_IDS = TAB_ORDER.map((t) => t.id as string);

export function parseStatusTab(value: string | null): StatusTabId {
  return value && TAB_IDS.includes(value) ? (value as StatusTabId) : 'drafts';
}

/**
 * The order posts are listed WITHIN one lane of a campaign, by the status chip they show.
 *
 * Deliberately the same sequence as {@link TAB_ORDER}, at a finer grain: this one names individual
 * approval states, that one names the tabs they group into. Both put unfinished work first and sink
 * the three finished states to the bottom, with Failed above them — it is the one status that still
 * needs a person, and buried under everything already published it is the easiest thing to miss.
 *
 * The three review states rank together — a post awaiting its second sign-off is still "In Review"
 * to everyone reading the lane, exactly as the `approvals` tab treats them.
 *
 * SCHEDULED sits where the pipeline puts it, between approved and published.
 */
export const POST_STATUS_SORT: PostDisplayState[] = [
  ApprovalState.DRAFT,
  ApprovalState.IN_REVIEW,
  ApprovalState.REVIEWED,
  ApprovalState.COMPLIANCE_CHECK,
  ApprovalState.REVISE,
  ApprovalState.APPROVED,
  ApprovalState.SCHEDULED,
  'SCHEDULING_ERROR',
  'FAILED',
  ApprovalState.PUBLISHED,
  ApprovalState.REJECTED,
];

const POST_STATUS_RANK = new Map<PostDisplayState, number>(
  POST_STATUS_SORT.map((state, i) => [state, i]),
);

/** Sort key for a post's chip. An unknown state sorts last rather than to the top. */
export function postStatusRank(state: PostDisplayState): number {
  return POST_STATUS_RANK.get(state) ?? POST_STATUS_SORT.length;
}

/** The shared sub-header styling for the filter rows ("Status", "Post Type", …). */
export const SUBHEADER_CLS = 'mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-muted';
